/*
 * Copyright (c) 2018 Well Solutions SRL. All Rights Reserved.
 * This software is the proprietary information of Well Solutions SRL.
 * Use is subject to license and non-disclosure terms
 */

import { JSONable } from '@irian-ro/abstract-resource';
import * as moment from 'moment';
import { FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { DateUtilsService } from './date-utils.service';

export class DateTimeWithTimezoneModel extends JSONable {
  dateTime: string | undefined | null = undefined;
  timezone: string | undefined | null = undefined;

  constructor(dateTime: string, timezone: string) {
    super();
    this.dateTime = dateTime;
    this.timezone = timezone;
  }

  override toString() {
    return moment(this.dateTime).format('YYYY-MM-DD HH:mm');
  }

  static createForm() {
    return new FormGroup({
      dateTime: new FormControl(null),
      timezone: new FormControl(null),
    });
  }
}

export const isDateTimeWithTimezoneInPast = (
  dateTimeWithTimezone: DateTimeWithTimezoneModel | null | undefined,
  offsetDays = 0
): ValidationErrors | null => {

  const dateOffset = new Date();
  dateOffset.setHours(0, 0, 0, 0);
  dateOffset.setDate(dateOffset.getDate() - offsetDays);

  if (!dateTimeWithTimezone || !dateTimeWithTimezone.dateTime || !dateTimeWithTimezone.timezone) {
    return { invalidDateTime: true };
  }

  return DateUtilsService.compareDates(
    DateUtilsService.toTimezone(dateTimeWithTimezone.dateTime, dateTimeWithTimezone.timezone),
    dateOffset.toISOString()
  ) < 0
    ? { beforeOffset: true }
    : null;
};

export const dateTimeWithTimezoneInPast = (dateTimeWithTimezone: DateTimeWithTimezoneModel) => {
  if (!dateTimeWithTimezone || !dateTimeWithTimezone.dateTime || !dateTimeWithTimezone.timezone) {
    return { invalidDateTime: true };
  }
  return DateUtilsService.compareDates(dateTimeWithTimezone.dateTime, new Date().toISOString()) < 0
    ? { dateTimeWithTimezoneInPast: {error: 'Date time is in the past'} }
    : null;
};

export const isDateTimeWithTimezoneAfter = (
  start: DateTimeWithTimezoneModel,
  end: DateTimeWithTimezoneModel
): ValidationErrors | null => {
  const startWithTimezone = DateUtilsService.toTimezone(start.dateTime, start.timezone);
  const endWithTimezone = DateUtilsService.toTimezone(end.dateTime, end.timezone);
  return DateUtilsService.compareDates(startWithTimezone, endWithTimezone) < 0
    ? null
    : { isDateTimeWithTimezoneAfter: true };
};

export const isDateTimeWithTimezoneEqualOrAfter = (start: DateTimeWithTimezoneModel, end: DateTimeWithTimezoneModel) => {

  const startWithTimezone = DateUtilsService.toTimezone(start.dateTime, start.timezone);
  const endWithTimezone = DateUtilsService.toTimezone(end.dateTime, end.timezone);
  return DateUtilsService.compareDates(startWithTimezone, endWithTimezone) <= 0
    ? null
    : { isDateTimeEqualOrAfter: true };
}

export const isDateTimeInUtcEqualOrAfter = (
  start: DateTimeWithTimezoneModel,
  end: DateTimeWithTimezoneModel
): ValidationErrors | null => {
  return DateUtilsService.compareDates(start.dateTime, end.dateTime) <= 0
    ? null
    : { isDateTimeEqualOrAfter: true };
};
